import {atom} from 'jotai';
import {atomWithStorage} from 'jotai/utils';

export interface ChatSearchResult {
    id: string | number;
    name?: string;
    url?: string;
    [key: string]: unknown;
}

export interface ChatMessage {
    id: string;
    role: 'user' | 'assistant';
    content: string;
    timestamp: Date;
    searchResults?: {
        results: ChatSearchResult[];
        type: 'product' | 'group' | 'material';
    };
}

export interface ChatContext {
    currentPage: string;
    pageType?: string;
    userId?: string;
    userName?: string;
    currentAsset?: {
        id: string;
        name: string;
        type: 'product' | 'group';
    };
}

// ============================================================================
// Project-specific chat history support
// ============================================================================

// Current active project name (null = general/non-editor chat)
export const lindaChatProjectAtom = atom<string | null>(null);

// Helper to get localStorage key for a project
const getProjectChatKey = (projectName: string) =>
    `linda-chat-project-${projectName}`;

// Save chat messages for a specific project
export const saveProjectChatHistory = (
    projectName: string,
    messages: ChatMessage[]
) => {
    if (typeof window === 'undefined') return;
    try {
        // Serialize messages, converting Date objects to strings
        const serialized = messages.map((m) => ({
            ...m,
            timestamp:
                m.timestamp instanceof Date
                    ? m.timestamp.toISOString()
                    : m.timestamp,
        }));
        localStorage.setItem(
            getProjectChatKey(projectName),
            JSON.stringify(serialized)
        );
    } catch (e) {
        console.warn('[Linda Chat] Failed to save project chat history:', e);
    }
};

// Serialized message format stored in localStorage
interface SerializedChatMessage extends Omit<ChatMessage, 'timestamp'> {
    timestamp: string;
}

// Load chat messages for a specific project
export const loadProjectChatHistory = (projectName: string): ChatMessage[] => {
    if (typeof window === 'undefined') return [];
    try {
        const stored = localStorage.getItem(getProjectChatKey(projectName));
        if (!stored) return [];
        const parsed: SerializedChatMessage[] = JSON.parse(stored);
        // Convert timestamp strings back to Date objects
        return parsed.map((m) => ({
            ...m,
            timestamp: new Date(m.timestamp),
        }));
    } catch (e) {
        console.warn('[Linda Chat] Failed to load project chat history:', e);
        return [];
    }
};

// List all saved project chat histories
export const listProjectChatHistories = (): string[] => {
    if (typeof window === 'undefined') return [];
    const projects: string[] = [];
    const prefix = 'linda-chat-project-';
    for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key?.startsWith(prefix)) {
            projects.push(key.substring(prefix.length));
        }
    }
    return projects;
};

// Delete chat history for a specific project
export const deleteProjectChatHistory = (projectName: string) => {
    if (typeof window === 'undefined') return;
    localStorage.removeItem(getProjectChatKey(projectName));
};

// ============================================================================
// Main chat atoms
// ============================================================================

// Chat panel open/closed state - persists across session
export const lindaChatOpenAtom = atomWithStorage<boolean>(
    'linda-chat-open',
    false
);

// Chat messages - persists across session (for general/non-project chat)
export const lindaChatMessagesAtom = atomWithStorage<ChatMessage[]>(
    'linda-chat-messages',
    []
);

// Loading state for API calls
export const lindaChatLoadingAtom = atom<boolean>(false);

// Chat panel width - persists across session
export const lindaChatWidthAtom = atomWithStorage<number>(
    'linda-chat-width',
    420
);

// Add message helper atom
export const addChatMessageAtom = atom(
    null,
    (_get, set, message: Omit<ChatMessage, 'id' | 'timestamp'>) => {
        const newMessage: ChatMessage = {
            ...message,
            id: Math.random().toString(36).substring(2, 11),
            timestamp: new Date(),
        };
        // Use functional update to avoid race conditions
        set(lindaChatMessagesAtom, (prev) => [...prev, newMessage]);
        return newMessage;
    }
);

// Clear conversation helper atom
export const clearChatMessagesAtom = atom(null, (_get, set) => {
    set(lindaChatMessagesAtom, []);
});

// Close chat helper atom
export const closeChatAtom = atom(null, (_get, set) => {
    set(lindaChatOpenAtom, false);
});

// Open chat helper atom
export const openChatAtom = atom(null, (_get, set) => {
    set(lindaChatOpenAtom, true);
});

// ============================================================================
// Project chat switching atoms
// ============================================================================

// Switch to a project-specific chat (or back to general chat if projectName is null)
export const switchChatProjectAtom = atom(
    null,
    (get, set, projectName: string | null) => {
        const currentProject = get(lindaChatProjectAtom);
        const currentMessages = get(lindaChatMessagesAtom);

        // Save current chat to the old project (if there was one)
        if (currentProject && currentMessages.length > 0) {
            saveProjectChatHistory(currentProject, currentMessages);
        }

        // Set the new project
        set(lindaChatProjectAtom, projectName);

        // Load chat history for the new project (or clear if null)
        if (projectName) {
            const projectMessages = loadProjectChatHistory(projectName);
            set(lindaChatMessagesAtom, projectMessages);
        } else {
            // Going back to general chat - could load general chat history here
            // For now, just clear messages
            set(lindaChatMessagesAtom, []);
        }
    }
);

// Save current chat to the active project (call this after each message)
export const saveCurrentProjectChatAtom = atom(null, (get) => {
    const currentProject = get(lindaChatProjectAtom);
    const currentMessages = get(lindaChatMessagesAtom);

    if (currentProject && currentMessages.length > 0) {
        saveProjectChatHistory(currentProject, currentMessages);
    }
});
